import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { type Crop, type PixelCrop } from 'react-image-crop';
import { useImageRef } from './ImageRefContext';

interface RegionEditContextValue {
  isRegionSelectMode: boolean;
  setIsRegionSelectMode: (mode: boolean) => void;
  regionCrop: Crop | undefined;
  setRegionCrop: (crop: Crop | undefined) => void;
  regionCompletedCrop: PixelCrop | undefined;
  setRegionCompletedCrop: (crop: PixelCrop | undefined) => void;
  regionAspect: number | undefined;
  setRegionAspect: (aspect: number | undefined) => void;
  editRegion: { x: number; y: number; width: number; height: number } | null;
  handleClearRegion: () => void;
  setDirectEditRegion: (region: { x: number; y: number; width: number; height: number } | null) => void;
}

const RegionEditContext = createContext<RegionEditContextValue | null>(null);

export const useRegionEdit = (): RegionEditContextValue => {
  const ctx = useContext(RegionEditContext);
  if (!ctx) throw new Error('useRegionEdit must be used within a RegionEditProvider');
  return ctx;
};

export const RegionEditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { imgRef, renderedSize } = useImageRef();

  const [isRegionSelectMode, setIsRegionSelectMode] = useState<boolean>(false);
  const [regionCrop, setRegionCrop] = useState<Crop>();
  const [regionCompletedCrop, setRegionCompletedCrop] = useState<PixelCrop>();
  const [regionAspect, setRegionAspect] = useState<number | undefined>(1);
  const [directEditRegion, setDirectEditRegion] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const editRegion = useMemo(() => {
    if (!isRegionSelectMode) return null;
    if (directEditRegion) return directEditRegion;
    if (!regionCompletedCrop || !imgRef.current || regionCompletedCrop.width === 0 || regionCompletedCrop.height === 0) return null;
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    return {
      x: Math.round(regionCompletedCrop.x * scaleX),
      y: Math.round(regionCompletedCrop.y * scaleY),
      width: Math.round(regionCompletedCrop.width * scaleX),
      height: Math.round(regionCompletedCrop.height * scaleY),
    };
  }, [directEditRegion, regionCompletedCrop, isRegionSelectMode, imgRef]);

  // Sync directEditRegion → crop component state so the visual overlay renders
  useEffect(() => {
    if (!directEditRegion || !imgRef.current) return;
    const nw = imgRef.current.naturalWidth;
    const nh = imgRef.current.naturalHeight;
    if (!nw || !nh) return;

    setRegionCrop({
      x: (directEditRegion.x / nw) * 100,
      y: (directEditRegion.y / nh) * 100,
      width: (directEditRegion.width / nw) * 100,
      height: (directEditRegion.height / nh) * 100,
      unit: '%',
    });

    const dw = renderedSize?.width ?? imgRef.current.width;
    const dh = renderedSize?.height ?? imgRef.current.height;
    if (!dw || !dh) return;
    setRegionCompletedCrop({
      x: Math.round(directEditRegion.x * (dw / nw)),
      y: Math.round(directEditRegion.y * (dh / nh)),
      width: Math.round(directEditRegion.width * (dw / nw)),
      height: Math.round(directEditRegion.height * (dh / nh)),
      unit: 'px',
    });
  }, [directEditRegion, imgRef, renderedSize]);

  const handleClearRegion = useCallback(() => {
    setRegionCrop(undefined);
    setRegionCompletedCrop(undefined);
    setDirectEditRegion(null);
  }, []);

  return (
    <RegionEditContext.Provider value={{
      isRegionSelectMode,
      setIsRegionSelectMode,
      regionCrop,
      setRegionCrop,
      regionCompletedCrop,
      setRegionCompletedCrop,
      regionAspect,
      setRegionAspect,
      editRegion,
      handleClearRegion,
      setDirectEditRegion,
    }}>
      {children}
    </RegionEditContext.Provider>
  );
};
